import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute, { GuestOnly } from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import PublicLayout from "@/components/layout/PublicLayout";
import PortalLayout from "@/components/layout/PortalLayout";
import AdminLayout from "@/components/admin/AdminLayout";
import Landing from "@/pages/public/Landing";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Dashboard from "@/pages/parent/Dashboard";
import Consent from "@/pages/parent/Consent";
import Account from "@/pages/Account";
import Assessment from "@/pages/parent/Assessment";
import ReportView from "@/pages/ReportView";
import Reports from "@/pages/admin/Reports";
import Overview from "@/pages/admin/Overview";
import CreateAccount from "@/pages/admin/CreateAccount";
import Parents from "@/pages/admin/Parents";
import ParentDetail from "@/pages/admin/ParentDetail";
import Students from "@/pages/admin/Students";
import Consents from "@/pages/admin/Consents";
import Payments from "@/pages/admin/Payments";
import Schools from "@/pages/admin/Schools";
import QuestionBank from "@/pages/admin/QuestionBank";
import CareerClusters from "@/pages/admin/CareerClusters";
import HigherEd from "@/pages/admin/HigherEd";
import Settings from "@/pages/admin/Settings";
import ConsentVersions from "@/pages/admin/ConsentVersions";
import Admins from "@/pages/admin/Admins";
import { EmailLogs, Messages, AuditLog } from "@/pages/admin/Logs";

const ADMIN_ROLES = ["admin", "superadmin"];

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sample-report" element={<ReportView sample />} />
          </Route>
          <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/signup" element={<GuestOnly><Signup /></GuestOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword mode="reset" />} />
          <Route path="/set-password" element={<ResetPassword mode="setup" />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route element={<ProtectedRoute roles={["parent"]}><PortalLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/consent/:studentId" element={<Consent />} />
            <Route path="/account" element={<Account />} />
            <Route path="/dashboard/assessment/:sessionId" element={<Assessment />} />
            <Route path="/dashboard/report/:studentId" element={<ReportView />} />
          </Route>
          <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Overview />} />
            <Route path="create-account" element={<CreateAccount />} />
            <Route path="parents" element={<Parents />} />
            <Route path="parents/:id" element={<ParentDetail />} />
            <Route path="students" element={<Students />} />
            <Route path="consents" element={<Consents />} />
            <Route path="payments" element={<Payments />} />
            <Route path="schools" element={<Schools />} />
            <Route path="question-bank" element={<QuestionBank />} />
            <Route path="career-clusters" element={<CareerClusters />} />
            <Route path="higher-education" element={<HigherEd />} />
            <Route path="settings" element={<Settings />} />
            <Route path="consent-versions" element={<ConsentVersions />} />
            <Route path="emails" element={<EmailLogs />} />
            <Route path="messages" element={<Messages />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:reportId" element={<ReportView admin />} />
            <Route path="admins" element={<ProtectedRoute roles={["superadmin"]}><Admins /></ProtectedRoute>} />
            <Route path="account" element={<Account />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieBanner />
        <Toaster position="top-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope" } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
